// import React from "react";
// import { Box, Grid, Typography, TextField, Button, Paper } from "@mui/material";
// import PublicLayout from "../../layouts/PublicLayout";
// import { Container } from "@mui/material";

// function ExploreAllFeatures(props) {
//   return (
//     <PublicLayout>
//       <Container maxWidth="lg">
//         <Box>Explore all features</Box>
//       </Container>
//     </PublicLayout>
//   );
// }

// export default ExploreAllFeatures;

import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import { motion } from "framer-motion";
import PublicLayout from "../../layouts/PublicLayout";

export default function ExploreAllFeatures() {
  return (
    <>
      <PublicLayout>
        <Container maxWidth="lg">
          {/* HERO */}
          <Box
            sx={{
              py: { xs: 8, md: 12 },
              textAlign: "center",
            }}
          >
            <Container maxWidth="md">
              <Typography variant="h3" fontWeight={800} mb={2}>
                Explore Every Feature
              </Typography>

              <Typography variant="h6" color="text.secondary" mb={4}>
                A complete restaurant POS system that covers everything —
                orders, billing, tables, menu, inventory, staff & analytics.
                Designed to work the way your business works.
              </Typography>
            </Container>
          </Box>

          {/* FEATURES GRID */}
          <Container maxWidth="lg" sx={{ mb: 12 }}>
            <Grid container spacing={4}>
              {[
                {
                  title: "Smart Ordering",
                  desc: "Punch orders in seconds with item suggestions, variants, modifiers and KOT routing.",
                  icon: "🧾",
                },
                {
                  title: "Multiple Billing Modes",
                  desc: "Dine-in, takeaway, delivery, token billing — all in one screen.",
                  icon: "💳",
                },
                {
                  title: "Table Management",
                  desc: "Drag-and-drop tables, merge/split bills, live occupancy status.",
                  icon: "🍽️",
                },
                {
                  title: "Menu Management",
                  desc: "Add items, prices, categories, taxes — update menu instantly.",
                  icon: "📋",
                },
                {
                  title: "Inventory",
                  desc: "Track stock, consumption, wastage, and set low-stock alerts.",
                  icon: "📦",
                },
                {
                  title: "Staff & Roles",
                  desc: "Granular permissions for cashiers, waiters, managers & owners.",
                  icon: "👤",
                },
                {
                  title: "Reports & Analytics",
                  desc: "Sales, GST reports, item-wise summary, hourly trends, cancellations & more.",
                  icon: "📊",
                },
                {
                  title: "Offline Mode",
                  desc: "Continue billing even during network outages — auto sync when online.",
                  icon: "⚡",
                },
              ].map((f, i) => (
                <Grid item size={{ xs: 12, md: 6 }} key={i}>
                  <Card
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      height: "100%",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h3" fontSize={40}>
                        {f.icon}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} mt={1} mb={1}>
                        {f.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {f.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>

          {/* IMAGE + TEXT SECTIONS */}
          {[
            {
              title: "Billing Built for Speed",
              desc: "Fastest POS interface for restaurants. Every action is one tap away.",
              img: "https://images.unsplash.com/photo-1556745757-8d76bdb6984b?q=80&w=2000",
              reverse: false,
            },
            {
              title: "Track Inventory Automatically",
              desc: "Every sale deducts stock automatically. Set low-stock alerts & generate purchase orders.",
              img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2000",
              reverse: true,
            },
            {
              title: "Smart Insights for Better Decisions",
              desc: "Identify best-selling items, peak hours, staff performance and more with visual dashboards.",
              img: "https://images.unsplash.com/photo-1556155092-8707de31f9c4?q=80&w=2000",
              reverse: false,
            },
          ].map((sec, i) => (
            <Box
              key={i}
              sx={{
                py: { xs: 6, md: 12 },
                backgroundColor: i % 2 === 0 ? "#fff" : "#f9fafb",
              }}
            >
              <Container maxWidth="lg">
                <Grid
                  container
                  spacing={6}
                  direction={sec.reverse ? "row-reverse" : "row"}
                  alignItems="center"
                >
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <motion.img
                      src={sec.img}
                      alt={sec.title}
                      style={{
                        width: "100%",
                        borderRadius: "16px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                      }}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <motion.div
                      initial={{ opacity: 0, x: sec.reverse ? 40 : -40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Typography variant="h4" fontWeight={800} mb={2}>
                        {sec.title}
                      </Typography>
                      <Typography variant="h6" color="text.secondary" mb={3}>
                        {sec.desc}
                      </Typography>

                      <Button variant="contained" size="large">
                        Learn More
                      </Button>
                    </motion.div>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          ))}

          {/* CTA */}
          <Box sx={{ py: 12, textAlign: "center" }}>
            <Container maxWidth="sm">
              <Typography variant="h4" fontWeight={800} mb={2}>
                Ready to see everything in action?
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={4}>
                Book a free demo and we’ll walk you through T-POS live.
              </Typography>
              <Button
                size="large"
                variant="contained"
                sx={{ px: 4, py: 1.5, mr: 2 }}
              >
                Take a Free Demo
              </Button>
              <Button size="large" variant="outlined" sx={{ px: 4, py: 1.5 }}>
                Get Started
              </Button>
            </Container>
          </Box>
        </Container>
      </PublicLayout>
    </>
  );
}
